import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  isEntityServerSidePaginated,
  isValidPositiveNumber,
  mergeRowIntoGroupedTableResults,
  tableRowsAtomFamily,
  tableRowsGroupValuesAtomFamily,
  type BookingDto,
  type IExtendableDto,
  type IRideDto,
  type RydeUiUnit,
} from "@ryde/shared";

import { useBookingsListActions } from "./bookings";
import { useStarredRows } from "./dynamic-tables";
import { useClearStaleRideEditDto, useRidesListActions } from "./rides";
import { useShiftServerRowsCount } from "./use-shift-server-rows-count";

export const useDynamicFormsListActions = (entity: RydeUiUnit) => {
  const [rows, setRows] = useRecoilState(tableRowsAtomFamily(entity));
  const { replaceBooking, margeBookingRow } = useBookingsListActions();
  const { replaceRide, mergeRideRow } = useRidesListActions();
  const { clearStaleRideEditDtoCacheForRide } = useClearStaleRideEditDto();
  const [tableRows, setTableRows] = useRecoilState(tableRowsAtomFamily(entity));
  const setGroupedTableRows = useSetRecoilState(
    tableRowsGroupValuesAtomFamily(entity),
  );
  const isServerPaginated = isEntityServerSidePaginated(entity);
  const { removeStarredIds } = useStarredRows(entity);
  const { shiftServerRowsCount } = useShiftServerRowsCount(entity);

  // TODO get rid of code duplication `useTableListActions`
  const removeRows = useEvent((ids: number[]): void => {
    const idsSet = new Set(ids);
    const updatedRows = rows?.filter(row => !idsSet.has(row.id));
    setRows(updatedRows);
    shiftServerRowsCount(-ids.length);
    removeStarredIds(ids);
  });

  const replaceTableRow = useEvent((row: IExtendableDto) => {
    if (entity === "booking") {
      replaceBooking(row as BookingDto);
    } else if (entity === "ride") {
      replaceRide(row as IRideDto);
      const rideId = row?.id;
      if (isValidPositiveNumber(rideId)) {
        clearStaleRideEditDtoCacheForRide(rideId);
      }
    } else {
      const updatedEntityRows = tableRows?.map(originalRow =>
        originalRow?.id === row.id ? row : originalRow,
      );
      setTableRows(updatedEntityRows);

      if (isServerPaginated) {
        setGroupedTableRows(groups =>
          mergeRowIntoGroupedTableResults({ groups, dto: row }),
        );
      }
    }
  });

  const mergeDataTableRow = useEvent((row: IExtendableDto) => {
    if (entity === "booking") {
      margeBookingRow(row as BookingDto);
    } else if (entity === "ride") {
      mergeRideRow(row as IRideDto);
    } else {
      const updatedEntityRows = tableRows?.map(originalRow =>
        originalRow?.id === row.id ? { ...originalRow, ...row } : originalRow,
      );
      setTableRows(updatedEntityRows);

      if (isServerPaginated) {
        setGroupedTableRows(groups =>
          mergeRowIntoGroupedTableResults({ groups, dto: row }),
        );
      }
    }
  });

  return {
    removeRows,
    replaceTableRow,
    mergeDataTableRow,
  };
};
